// reports/reportApi.ts
// NOTE: adjust the import path to your axios instance.
import { axiosInstance } from "../../utility/apiInstance";
import type { ReportFilters, ReportRow, SelectOption, ShipmentMode } from "./Types";

function toPayload(mode: ShipmentMode, filters: ReportFilters) {
  return {
    mode,
    dateType: filters.dateType,
    startDate: filters.startDate,
    endDate: filters.endDate,
    customers: filters.customers.map((c) => c.value),
    carriers: filters.carriers.map((c) => c.value),
  };
}

/**
 * Fetch the FULL filtered result set (no server pagination) so the table and
 * the exports share one dataset. Endpoint chosen by mode.
 */
export async function fetchReport(
  mode: ShipmentMode,
  filters: ReportFilters
): Promise<ReportRow[]> {
  const { data } = await axiosInstance.post(`/api/v1/report/${mode}`, toPayload(mode, filters));
  const list: ReportRow[] = data?.data ?? data ?? [];
  return list.map((row, i) => ({
    ...row,
    id: row.id ?? row.trackingNumber ?? `row-${i}`,
  }));
}

/**
 * Customer options -> GET /api/v1/master-contact-organization
 * value must match the backend `customerId` filter (the id stored on the
 * waybills). Field-name fallbacks are guesses — confirm against the response.
 */
export async function fetchCustomerOptions(): Promise<SelectOption[]> {
  try {
    const { data } = await axiosInstance.get(`/api/v1/master-contact-organization`);
    const list = data?.data ?? data ?? [];
    return list.map((c: any) => ({
      value: String(c.name),
      label:c.name,
    }));
  } catch {
    return [];
  }
}

/* ------------------------------------------------------------------ *
 * Carrier options — different source per mode:
 *   Air   -> GET /api/v1/allowed-airline      (airlines)
 *   Ocean -> GET /api/v1/ocean/shippingline   (shipping lines)
 *
 * IMPORTANT — the option `value` must match what the backend filters on:
 *   Air   filter: airlineId  { $in }  -> value = airline _id
 *   Ocean filter: carrierCode { $in } -> value = carrier code (SCAC)
 * Field-name fallbacks are guesses; confirm against your actual responses.
 * ------------------------------------------------------------------ */

/** Air carriers: allowed airlines. value = airline _id, label = airline name. */
export async function fetchAirlineOptions(): Promise<SelectOption[]> {
  const { data } = await axiosInstance.get(`/api/v1/allowed-airline`);
  const list = data?.data ?? data ?? [];
  return list.map((a: any) => ({
    value: String(a._id),
    label: a.airlineName,
  }));
}

/** Ocean carriers: shipping lines. value = carrier code, label = carrier name. */
export async function fetchShippingLineOptions(): Promise<SelectOption[]> {
  const { data } = await axiosInstance.get(`/api/v1/ocean/shippingline`);
  const list = data?.data ?? data ?? [];
  return list.map((s: any) => ({
    value: String(s.scacCode),
    label: s.scacCode,
  }));
}

/** Dispatch by mode; used by ReportPage. Best-effort (empty on failure). */
export async function fetchCarrierOptions(mode: ShipmentMode): Promise<SelectOption[]> {
  try {
    return mode === "air" ? await fetchAirlineOptions() : await fetchShippingLineOptions();
  } catch {
    return [];
  }
}